import { CommandItem as _CommandItem } from 'cmdk';
import { ComponentType, ReactNode } from 'react';
import styled from 'styled-components';

import { Text } from '../../../cdl/Text/Text';
import { theme } from '../../ui/theme';

interface StyledCommandItemProps extends CommandItemProps {
    destructive?: boolean;
}

const LeadingVisual = styled.div<{ $iconVisibility: 'always' | 'selected' }>`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    opacity: ${({ $iconVisibility }) => ($iconVisibility === 'selected' ? 0 : 1)};

    [data-selected='true'] & {
        opacity: 1;
    }
`;

const StyledCommandItem = styled(_CommandItem)<StyledCommandItemProps>`
    content-visibility: auto;
    cursor: pointer;

    min-width: 0;

    display: flex;
    align-items: center;
    gap: 12px;
    padding: ${theme.space[8]} 12px;
    color: ${theme.colors.foreground.default};
    user-select: none;
    will-change: background, color;
    transition: all 150ms ease;
    transition-property: none;
    border-radius: ${theme.radii[4]};

    &[data-selected='true'] {
        background: ${(props) =>
            props.destructive ? theme.colors.negative.background : theme.colors.accent.background};
        color: ${(props) => (props.destructive ? theme.colors.negative.emphasis : theme.colors.accent.emphasis)};

        svg {
            color: ${(props) => (props.destructive ? theme.colors.negative.emphasis : theme.colors.accent.emphasis)};
        }
    }

    svg {
        color: ${theme.colors.foreground.default};
    }
`;

interface CommandItemProps {
    Icon?: ComponentType<{ width?: number; height?: number }>;
    children: ReactNode;
    onSelect?: (value: string) => void;
    value?: string;
    forceMount?: boolean;
    destructive?: boolean;
    trailingVisual?: ReactNode;
    iconVisibility?: 'always' | 'selected';
}

export const CommandItem = ({
    Icon,
    children,
    onSelect,
    trailingVisual,
    iconVisibility = 'always',
    ...rest
}: CommandItemProps) => {
    return (
        <StyledCommandItem onSelect={onSelect} {...rest}>
            {Icon ? (
                <LeadingVisual $iconVisibility={iconVisibility}>
                    <Icon width={16} height={16} />
                </LeadingVisual>
            ) : null}
            <Text
                variant="small"
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {children}
            </Text>
            {trailingVisual ? <div>{trailingVisual}</div> : null}
        </StyledCommandItem>
    );
};
